import React, {useContext, useMemo, ReactElement} from "react";
import {inject, observer} from "mobx-react";
import { RootStore } from 'stores';
import _ from "lodash";
import {
    AreaField,
    getOptions,IntegrationSftpVendorType,
    isTabletPortraitDown, ProviderIntegrationSftp, SelectField,
    ToastContext
} from "@enter/core";
import * as S from "./shared.styles";
import * as SP from "./settings-page.styles";
import {Icon} from "@mdi/react";
import {mdiArrowLeft, mdiPlus, mdiDelete, mdiClipboardArrowUpOutline} from "@mdi/js";
import { DialogContext } from 'services';
import styled from "@emotion/styled/macro";

import {Formik, FormikHelpers, FormikProps, Form, Field} from "formik";
import * as Yup from "yup";
import copy from 'copy-text-to-clipboard';
import {
    ErrorWarningLabel,
    PaddedListItem,
    SideBySideInputGroup,
    SideBySideValue,
    ZeroPadFormField
} from "./settings-page.styles";

const ActionsContainer = styled.div`
  padding-top:10px;
`;

export const SftpSettings: React.FunctionComponent<{}> = inject('rootStore')(
    observer(
        (props: {}): React.ReactElement => {
            const [errorState, setErrorState] = React.useState<string|undefined>();
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const toastContext = useContext(ToastContext);
            const dialogContext = useContext(DialogContext);

            const { settingsStore } = rootStore;

            const onFormSubmit = async (values: FormProps, formik: FormikHelpers<FormProps>) => {
                formik.setSubmitting(true);
                setErrorState(undefined);
                try {
                    const previousId: string|undefined = settingsStore.integrationSftp ? settingsStore.integrationSftp.id : undefined;
                    await settingsStore.saveSftpIntegration(values.vendorType, values.publicKey, previousId);
                } catch(e) {
                    if(e instanceof Response) {
                        const resJson = await e.json();
                        setErrorState(resJson.message + " (trace id: " + resJson.trace_id + ")");
                        console.log(resJson);
                    }
                    throw e;
                } finally {
                    formik.setSubmitting(false);
                }
            };

            type FormProps = {
                publicKey: string;
                vendorType: IntegrationSftpVendorType;
            }

            const SftpSchema = Yup.object().shape({
                publicKey: Yup.string().required('Public key is required'),
                vendorType: Yup.mixed<IntegrationSftpVendorType>().oneOf(Object.values(IntegrationSftpVendorType))
            });

            const initialValues: FormProps = useMemo(() => {
                return {
                    publicKey: settingsStore.integrationSftp && settingsStore.integrationSftp.publicKey ? settingsStore.integrationSftp.publicKey : 'ssh-rsa AAAAAAAAAAAAAAAAAAAAAAAAEXAMPLE= user@example.com',
                    vendorType: settingsStore.integrationSftp && settingsStore.integrationSftp.vendorType ? settingsStore.integrationSftp.vendorType :  IntegrationSftpVendorType.ECW
                };
            }, []);


            function sftpForm() : ReactElement {
                return  <Formik initialValues={initialValues} validationSchema={SftpSchema} onSubmit={onFormSubmit}>
                    {(formik: FormikProps<FormProps>) => (
                        <SP.DialogBody>
                            <ErrorWarningLabel>{errorState}</ErrorWarningLabel>
                            <ul>
                                <SP.PaddedListItem>To generate a key on Windows, from command prompt, run <i>ssh-keygen -t rsa -b 2048 -C "your_email@example.com"</i> and copy the contents from <i>C:\Users\YOU\.ssh\id_rsa.pub</i></SP.PaddedListItem>
                                <SP.PaddedListItem>To generate a key on a Mac, run <i>ssh-keygen -t rsa -b 2048 -C "your_email@example.com"</i> and copy the contents from <i>/Users/YOU/.ssh/id_rsa.pub</i></SP.PaddedListItem>
                            </ul>
                            <SP.SideBySideInputGroup>
                                <S.Label>Host: </S.Label>
                                <SP.SideBySideValue>sftp.enter.health</SP.SideBySideValue>
                            </SP.SideBySideInputGroup>
                            <SP.SideBySideInputGroup>
                                <S.Label>Username: </S.Label>
                                <SP.SideBySideValue>{settingsStore.integrationSftp ? settingsStore.integrationSftp.username : 'N/A'}</SP.SideBySideValue>
                            </SP.SideBySideInputGroup>
                            <S.InputGroup>
                                <S.Label>Vendor Type</S.Label>
                                <SP.ZeroPadFormField
                                    name="vendorType"
                                    component={SelectField}
                                    options={getOptions(IntegrationSftpVendorType)}
                                    required={true}
                                />
                            </S.InputGroup>
                            <SP.InputGroup>
                                <SP.Label>Public Key</SP.Label>
                                <SP.FormField name="publicKey" component={AreaField} />
                            </SP.InputGroup>
                            <ActionsContainer>
                                <S.SubCategoryAddButton variant="primary" type="submit" onClick={formik.submitForm} disabled={formik.isSubmitting}>
                                    {formik.isSubmitting ? <SP.Pending size="20px" /> : <>Save SFTP Configuration</>}
                                </S.SubCategoryAddButton>
                            </ActionsContainer>
                        </SP.DialogBody>
                    )}
                </Formik>
            }


            return <S.SubCategoryCol>
                <S.SubCategoryItem key="members">
                    <S.SubCategoryItemRow>
                        <S.SubCategoryH1>
                            {isTabletPortraitDown() && (
                                <S.ReturnLink to="/settings">
                                    {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                </S.ReturnLink>
                            )}
                            SFTP Configuration
                        </S.SubCategoryH1>

                        {sftpForm()}

                    </S.SubCategoryItemRow>
                </S.SubCategoryItem>
            </S.SubCategoryCol>;
        },
    ),
);